import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Ic_user from "../../assets/images/icons/patientDefault.svg";
import { useToast } from "../../Services/ToastContext";
import { FILETYPESWithoutHeifHeic } from "../../Constants/fileConstants";
import GeneralButton from "../Buttons/GeneralButton";

interface ProfileImageUploaderProps {
  name: string;
  placeholder?: string;
  onFileChange?: (file: File) => void;
  onFileRemove?: () => void;
  imageSize?: string;
  defaultImage?: string | null;
  readOnly?: boolean;
  additionalText?: string;
}

const ProfileImageUploader: React.FC<ProfileImageUploaderProps> = ({
  placeholder = "",
  name,
  onFileChange,
  onFileRemove,
  imageSize = "w-16 h-16",
  defaultImage = Ic_user,
  readOnly = false,
  additionalText,
}) => {
  const { t } = useTranslation();
  const formContext = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentImage, setCurrentImage] = useState<string | undefined>(
    defaultImage || undefined,
  );
  const { showToast } = useToast();

  const {
    setValue,
    watch,
    formState: { errors },
  } = formContext || {};

  const watchedValue = watch ? watch(name) : undefined;

  useEffect(() => {
    if (watchedValue instanceof File) {
      const url = URL.createObjectURL(watchedValue);
      setCurrentImage(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (watchedValue === null) {
      setCurrentImage(Ic_user);
    } else if (defaultImage && !watchedValue) {
      setCurrentImage(defaultImage);
    } else {
      setCurrentImage(Ic_user);
    }
  }, [watchedValue, defaultImage]);

  const handleProfilePictureChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (readOnly) return;

    const file = event.target.files?.[0];

    if (file) {
      const allowedMimeTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/jfif",
        "image/bmp",
        "image/heif",
        "image/heic",
      ];

      if (!allowedMimeTypes.includes(file.type)) {
        showToast(t("unsupported_file_type"), "error");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showToast(t("file_size_exceeded"), "error");
        return;
      }

      setValue?.(name, file);
      if (onFileChange) {
        onFileChange(file);
      }
    }
  };

  const handleTextClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleRemoveClick = () => {
    if (readOnly) return;

    setValue?.(name, null);
    setCurrentImage(Ic_user);

    if (onFileRemove) {
      onFileRemove();
    }
  };

  const showRemoveButton = currentImage !== Ic_user;

  return (
    <div>
      {!formContext ? (
        <div className="flex items-center gap-8">
          <div
            className={`${imageSize} flex items-center justify-center rounded-full bg-blue-50`}
          >
            <img src={Ic_user} alt="Default profile" className="h-14 w-14" />
          </div>
          <button
            type="button"
            className="font-semibold text-secondaryDark"
            onClick={() => alert("Form context is missing!")}
          >
            {placeholder}
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-4">
            {currentImage !== Ic_user ? (
              <div className="relative">
                <img
                  src={currentImage}
                  alt="Profile"
                  className={`${imageSize} rounded-full border-2 border-primary object-cover`}
                />
                {/* {showRemoveButton && !readOnly && (
                  <button
                    type="button"
                    className="absolute right-0 top-0 rounded-[100%] aspect-square bg-error px-2 text-white"
                    onClick={handleRemoveClick}
                    aria-label={t("remove")}
                  >
                    &times;
                  </button>
                )} */}
              </div>
            ) : (
              <div
                className={`${imageSize} flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-text-muted bg-blue-50`}
              >
                <label className="flex h-full w-full cursor-pointer items-center justify-center overflow-hidden">
                  <img
                    src={Ic_user}
                    alt="Default profile"
                    className="h-full w-full object-cover"
                  />
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleProfilePictureChange}
                    ref={fileInputRef}
                    aria-label={placeholder}
                    accept={FILETYPESWithoutHeifHeic}
                    disabled={readOnly}
                  />
                </label>
              </div>
            )}
            <div className="flex w-full flex-1 justify-between">
              <div className="flex flex-col gap-2">
                <p className="font-semibold text-secondaryDark">
                  {placeholder}
                </p>
                <p className="text-xs text-body-text">{t("optional")}</p>
              </div>
              {showRemoveButton ? (
                !readOnly && (
                  <div className="flex items-center gap-2">
                    <GeneralButton
                      color="red"
                      onClick={handleRemoveClick}
                      title={t("remove")}
                      className="!py-1 !text-xs !font-bold"
                    />
                    <label className="cursor-pointer overflow-hidden">
                      <button
                        type="button"
                        className="rounded-lg bg-primary-click px-2 py-1 text-xs font-bold text-primary"
                        onClick={handleTextClick}
                        disabled={readOnly}
                      >
                        {t("change_photo")}
                      </button>
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleProfilePictureChange}
                        ref={fileInputRef}
                        aria-label={placeholder}
                        accept={FILETYPESWithoutHeifHeic}
                        disabled={readOnly}
                      />
                    </label>
                  </div>
                )
              ) : (
                <div className="flex flex-col gap-2">
                  <GeneralButton
                    color="lite"
                    onClick={handleTextClick}
                    title={t("add_profile_picture")}
                    className="!py-1 !text-xs !font-bold"
                  />
                </div>
              )}

              {additionalText && (
                <p className="mt-1 text-xs text-gray-500">{additionalText}</p>
              )}
            </div>
          </div>
          {errors?.[name] && (
            <p className="mt-1 text-xs text-error">
              {errors[name]?.message as string}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileImageUploader;
